// SourcesPanel - Split-view dock for viewing PDF sources with page navigation
// Used in ChatWidget/ChatPopout to display citations from AI responses

import React, { useState, useCallback, useMemo } from 'react';
import { FileText, ChevronRight, ChevronDown, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { PDFViewer } from '@/components/contacts/PDFViewer';
import type { CitationMetadata } from '@/types/chat';
import { supabase } from '@/utils/supabase';

/** Grouped document with all its citations */
interface DocumentGroup {
  documentId: string;
  title: string;
  citations: CitationMetadata[];
  bestRelevance: number;
  source_file: string | null;
}

interface SourcesPanelProps {
  /** List of citations from AI message metadata */
  citations: CitationMetadata[];
  /** Currently selected citation (controls PDF viewer) */
  selectedCitation: CitationMetadata | null;
  /** Callback when a citation is selected */
  onSelectCitation: (citation: CitationMetadata) => void;
  /** Callback to close the panel */
  onClose: () => void;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Format page range for display
 */
function formatPageRange(pageStart: number | null, pageEnd: number | null): string {
  if (pageStart === null) {
return '';
}
  if (pageEnd === null || pageEnd === pageStart) {
return `Page ${pageStart}`;
}
  return `Pages ${pageStart}-${pageEnd}`;
}

/**
 * Get relevance badge color
 */
function getRelevanceBadge(relevance: number): { label: string; variant: 'default' | 'secondary' | 'outline' } {
  if (relevance >= 0.75) {
return { label: 'High', variant: 'default' };
}
  if (relevance >= 0.5) {
return { label: 'Medium', variant: 'secondary' };
}
  return { label: 'Low', variant: 'outline' };
}

/**
 * Document group item with expandable page references
 */
interface DocumentGroupItemProps {
  group: DocumentGroup;
  selectedCitation: CitationMetadata | null;
  isExpanded: boolean;
  index: number;
  onToggleExpand: () => void;
  onSelectCitation: (citation: CitationMetadata) => void;
}

function DocumentGroupItem({
  group,
  selectedCitation,
  isExpanded,
  index,
  onToggleExpand,
  onSelectCitation
}: DocumentGroupItemProps) {
  const relevanceBadge = getRelevanceBadge(group.bestRelevance);
  const isDocumentSelected = selectedCitation?.document_id === group.documentId;
  const hasMultiplePages = group.citations.length > 1;

  // Get unique pages from citations, sorted (dedupe by page number)
  const pageRefs = useMemo(() => {
    const seenPages = new Set<number>();
    const pages: { page: number; citation: CitationMetadata }[] = [];
    for (const c of group.citations) {
      if (c.page_start !== null && !seenPages.has(c.page_start)) {
        seenPages.add(c.page_start);
        pages.push({ page: c.page_start, citation: c });
      }
    }
    return pages.sort((a, b) => a.page - b.page);
  }, [group.citations]);

  return (
    <div
      className={cn(
        'rounded-lg border transition-all',
        isDocumentSelected
          ? 'bg-primary/10 border-primary/30 ring-1 ring-primary/20'
          : 'bg-background border-border hover:border-primary/20'
      )}
    >
      {/* Document header */}
      <button
        onClick={() => {
          if (hasMultiplePages) {
            onToggleExpand();
          } else {
            onSelectCitation(group.citations[0]);
          }
        }}
        className="w-full text-left p-3 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded-lg"
      >
        <div className="flex items-start gap-2">
          <div className={cn(
            'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium',
            isDocumentSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'
          )}>
            {index + 1}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
              <span className="font-medium text-sm truncate">{group.title}</span>
            </div>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={relevanceBadge.variant} className="text-[10px] h-4 px-1.5">
                {Math.round(group.bestRelevance * 100)}% match
              </Badge>
              {hasMultiplePages && (
                <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                  {group.citations.length} references
                </Badge>
              )}
            </div>
          </div>
          {hasMultiplePages ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            )
          ) : (
            <ChevronRight className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              isDocumentSelected && 'text-primary'
            )} />
          )}
        </div>
      </button>

      {/* Expanded page references - only show when page data exists */}
      {isExpanded && hasMultiplePages && pageRefs.length > 0 && (
        <div className="px-3 pb-3 pt-1 border-t border-border/50">
          <div className="text-xs text-muted-foreground mb-2">Jump to page:</div>
          <div className="flex flex-wrap gap-1.5">
            {pageRefs.map(({ page, citation }) => (
              <button
                key={citation.chunk_id}
                onClick={() => onSelectCitation(citation)}
                className={cn(
                  'px-2 py-1 text-xs rounded-md border transition-colors',
                  'hover:bg-primary/10 hover:border-primary/30',
                  'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
                  selectedCitation?.chunk_id === citation.chunk_id
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-muted/50 border-border'
                )}
              >
                p.{page}
              </button>
            ))}
          </div>
          {/* Show preview of selected citation within this group */}
          {selectedCitation && selectedCitation.document_id === group.documentId && (
            <p className="text-xs text-muted-foreground mt-2 line-clamp-2 italic">
              "{selectedCitation.preview}"
            </p>
          )}
        </div>
      )}

      {/* Fallback when page data unavailable - clickable to open document */}
      {isExpanded && hasMultiplePages && pageRefs.length === 0 && (
        <div className="px-3 pb-3 pt-1 border-t border-border/50">
          <button
            onClick={() => onSelectCitation(group.citations[0])}
            className="text-xs text-primary hover:underline focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1 rounded"
          >
            Open document (page locations unavailable)
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * SourcesPanel - Main component
 * Displays a list of citations and an embedded PDF viewer
 */
export function SourcesPanel({
  citations,
  selectedCitation,
  onSelectCitation,
  onClose,
  className
}: SourcesPanelProps) {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [loadedSourceKey, setLoadedSourceKey] = useState<string | null>(null);
  const [expandedDocuments, setExpandedDocuments] = useState<Set<string>>(new Set());

  // Group citations by document_id, preserving all page references
  const groupedCitations = useMemo((): DocumentGroup[] => {
    const groups = new Map<string, CitationMetadata[]>();
    for (const c of citations) {
      const existing = groups.get(c.document_id) || [];
      existing.push(c);
      groups.set(c.document_id, existing);
    }
    return Array.from(groups.entries()).map(([docId, cites]) => ({
      documentId: docId,
      title: cites[0].title,
      citations: cites.sort((a, b) => (a.page_start ?? 0) - (b.page_start ?? 0)),
      bestRelevance: Math.max(...cites.map((c) => c.relevance)),
      source_file: cites[0].source_file
    }));
  }, [citations]);

  // Toggle document expansion
  const toggleExpanded = useCallback((documentId: string) => {
    setExpandedDocuments((prev) => {
      const next = new Set(prev);
      if (next.has(documentId)) {
        next.delete(documentId);
      } else {
        next.add(documentId);
      }
      return next;
    });
  }, []);

  // Load PDF from Supabase storage when citation changes
  const loadPdf = useCallback(async (citation: CitationMetadata) => {
    if (!citation.source_file) {
      setPdfError('No source file available');
      setPdfUrl(null);
      setLoadedSourceKey(null);
      return;
    }

    if (loadedSourceKey === citation.source_file && pdfUrl) {
      return;
    }

    setPdfLoading(true);
    setPdfError(null);
    setPdfUrl(null);

    try {
      let bucket = 'ai-documents';
      let storagePath = citation.source_file;

      if (citation.source_file.startsWith('investor_document:')) {
        const docId = citation.source_file.replace('investor_document:', '');
        const { data, error } = await supabase
          .from('investor_documents')
          .select('storage_path, storage_bucket')
          .eq('id', docId)
          .single();

        if (error || !data?.storage_path) {
          throw new Error('Investor document storage path not found');
        }

        storagePath = data.storage_path;
        bucket = data.storage_bucket || 'documents';
      }

      // Get signed URL for the PDF from Supabase storage
      const { data, error } = await supabase.storage
        .from(bucket)
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) {
        throw error;
      }

      setPdfUrl(data.signedUrl);
      setLoadedSourceKey(citation.source_file);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Failed to load PDF');
      setPdfUrl(null);
      setLoadedSourceKey(null);
    } finally {
      setPdfLoading(false);
    }
  }, [loadedSourceKey, pdfUrl]);

  // Handle citation selection
  const handleSelectCitation = useCallback((citation: CitationMetadata) => {
    onSelectCitation(citation);
    loadPdf(citation);
  }, [onSelectCitation, loadPdf]);

  // Load initial PDF if selectedCitation is set
  React.useEffect(() => {
    if (selectedCitation) {
      loadPdf(selectedCitation);
      return;
    }

    setPdfUrl(null);
    setPdfError(null);
    setLoadedSourceKey(null);
  }, [selectedCitation, loadPdf]);

  return (
    <div className={cn('flex flex-col h-full bg-background', className)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" />
          <h3 className="font-semibold text-sm">Sources</h3>
          <Badge variant="secondary" className="text-xs">
            {groupedCitations.length}
          </Badge>
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close sources panel</span>
        </Button>
      </div>

      {/* Content area - split between list and viewer */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* Document list (top section, scrollable) */}
        <div className="flex-shrink-0 border-b max-h-[40%]">
          <ScrollArea className="h-full">
            <div className="p-3 space-y-2">
              {groupedCitations.map((group, index) => (
                <DocumentGroupItem
                  key={group.documentId}
                  group={group}
                  selectedCitation={selectedCitation}
                  isExpanded={expandedDocuments.has(group.documentId)}
                  index={index}
                  onToggleExpand={() => toggleExpanded(group.documentId)}
                  onSelectCitation={handleSelectCitation}
                />
              ))}
            </div>
          </ScrollArea>
        </div>

        {/* PDF Viewer (bottom section, takes remaining space) */}
        <div className="flex-1 min-h-0 relative">
          {pdfLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-muted/50">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {pdfError && !pdfLoading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4">
              <FileText className="h-12 w-12 text-muted-foreground mb-3" />
              <p className="text-sm text-muted-foreground mb-2">Failed to load PDF</p>
              <p className="text-xs text-muted-foreground/70">{pdfError}</p>
              {selectedCitation?.source_file && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-4"
                  onClick={() => loadPdf(selectedCitation)}
                >
                  Retry
                </Button>
              )}
            </div>
          )}

          {!pdfUrl && !pdfLoading && !pdfError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center p-4 text-muted-foreground">
              <FileText className="h-12 w-12 mb-3" />
              <p className="text-sm">Select a source to view the document</p>
            </div>
          )}

          {pdfUrl && !pdfLoading && !pdfError && (
            <PDFViewer
              url={pdfUrl}
              className="h-full"
              showBottomControls={true}
              showThumbnails={false}
              initialPage={selectedCitation?.page_start ?? 1}
              singlePageMode={true}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default SourcesPanel;
